import { useLocalization } from "@fluent/react";
import { Button, Row } from "@narmi/design_system";
import { useState } from "react";
import { BackButton } from "@lending/components/BackButton/BackButton";
import { OptionCard, OptionCardGroup } from "@lending/components/OptionCard";
import { PageHeader } from "@lending/components/PageHeader";
import { useSelectVerifiedIdentityChallengeEmailMutation } from "@lending/hooks/queries/VerifiedIdentityChallenge/useSelectVerifiedIdentityChallengeEmailMutation";
import { useNavigateWithSearch } from "@lending/hooks/useNavigateWithSearch";
import { useToastsContext } from "@lending/hooks/useToasts";
import { usePortalLogin } from "../PortalLoginContext";

/**
 * Second step, shown only when more than one primary applicant email matched the
 * submitted identity.
 *
 * Options are rendered from the masked addresses the backend returned. The real
 * addresses are never sent to the client; selection posts back the opaque token
 * and the server resolves it.
 */
export function SelectEmailScreen() {
  const { l10n } = useLocalization();
  const navigate = useNavigateWithSearch();
  const { challenge, setChallenge } = usePortalLogin();
  const { showErrorToast } = useToastsContext();
  const [selectedToken, setSelectedToken] = useState<string | null>(null);

  const { mutateAsync: selectEmail, isPending } = useSelectVerifiedIdentityChallengeEmailMutation();

  async function handleContinue() {
    // The button stays enabled so the requirement is stated on click rather
    // than left for the applicant to infer from a dead control.
    if (!selectedToken) {
      showErrorToast(
        l10n.getString(
          "portal-login-select-email-required",
          null,
          "Select an email address to continue"
        )
      );
      return;
    }

    if (!challenge) {
      return;
    }

    try {
      // The code is only generated once an email is chosen, so this response is
      // where the demo code appears for the multi-email path.
      const result = await selectEmail({
        params: { path: { uuid: challenge.challengeId } },
        body: { email_option_token: selectedToken },
      });

      const selectedMaskedEmail =
        challenge.emailOptions.find((option) => option.token === selectedToken)?.maskedEmail ??
        null;

      setChallenge({ ...challenge, selectedMaskedEmail, demoCode: result.code ?? null });
      void navigate("../verify");
    } catch {
      showErrorToast(
        l10n.getString(
          "portal-login-generic-error",
          null,
          "We couldn't verify that information. Please check your details and try again."
        )
      );
    }
  }

  return (
    <>
      <PageHeader
        heading={l10n.getString("portal-login-select-email-heading", null, "Select email")}
        subheading={l10n.getString(
          "portal-login-select-email-subheading",
          null,
          "Your verification code will be sent to this address."
        )}
      />

      <OptionCardGroup stacked>
        {challenge?.emailOptions.map((option) => (
          <OptionCard
            key={option.token}
            icon={<span className="narmi-icon-mail fontSize--l" />}
            label={option.maskedEmail}
            iconShape="square"
            boldLabel
            isSelected={selectedToken === option.token}
            onClick={() => {
              setSelectedToken(option.token);
            }}
          />
        ))}
      </OptionCardGroup>

      <div className="margin--top--xxl">
        <Row alignItems="center" justifyContent="space-between">
          <BackButton
            onClick={() => {
              void navigate("/portal/login");
            }}
          />
          <Button
            label={l10n.getString("button-label-continue", null, "Continue")}
            onClick={() => void handleContinue()}
            isLoading={isPending}
          />
        </Row>
      </div>
    </>
  );
}
